// src/utils/cashFlow.js
//
// True cash carry-forward for the Weekly Planner.
//
// The planner used to evaluate each week in isolation (Income − Expenses),
// which made zero-income weeks look like the user had no money even though
// last week's surplus is still in the bank. These helpers chain the weeks:
//
//   Beginning Cash + Income − Expenses = Ending Cash
//   Ending Cash of week N  →  Beginning Cash of week N+1
//
// The chain is a pure calculation over the per-week income/expense arrays the
// planner already produces, so it works identically for weekly, bi-weekly and
// monthly pay schedules and for any number of income sources — those only
// affect how the income lands in the week buckets, not the carry-forward.
//
// `startingCash` is the money already on hand entering week 1 (for example
// the previous month's ending balance). It is user-editable and persisted in
// plannerState.carryForward so the chain can continue indefinitely across
// months: enter last month's ending cash as this month's starting cash.

/**
 * Build the week-by-week carry-forward chain.
 *
 * @param {number[]} weeklyIncome    Income per week (any length).
 * @param {number[]} weeklyExpenses  Planned expenses per week (same length).
 * @param {number}   startingCash    Cash on hand entering the first week.
 * @returns {Array<{ week, beginningCash, income, expenses, endingCash }>}
 */
export function calculateCarryForward(weeklyIncome = [], weeklyExpenses = [], startingCash = 0) {
  const weekCount = Math.max(weeklyIncome.length, weeklyExpenses.length);
  const rows = [];
  let beginningCash = parseFloat(startingCash) || 0;

  for (let i = 0; i < weekCount; i++) {
    const income = parseFloat(weeklyIncome[i]) || 0;
    const expenses = parseFloat(weeklyExpenses[i]) || 0;
    const endingCash = beginningCash + income - expenses;
    rows.push({
      week: i + 1,
      beginningCash,
      income,
      expenses,
      endingCash
    });
    beginningCash = endingCash;
  }

  return rows;
}

/** Ending cash of the final week (what carries into next month), or the
 *  starting cash when there are no weeks. */
export function getMonthEndingCash(weeklyIncome = [], weeklyExpenses = [], startingCash = 0) {
  const rows = calculateCarryForward(weeklyIncome, weeklyExpenses, startingCash);
  return rows.length > 0 ? rows[rows.length - 1].endingCash : (parseFloat(startingCash) || 0);
}

/**
 * Read the persisted carry-forward settings out of plannerState, providing
 * safe defaults for budgets saved before this feature existed.
 */
export function getCarryForwardSettings(plannerState = {}) {
  const stored = plannerState?.carryForward;
  return {
    enabled: stored?.enabled !== false, // default ON — it is the truthful view
    startingCash: parseFloat(stored?.startingCash) || 0
  };
}
